import React from "react";
import { fmtPct, heatColor } from "./stats";

// Agent × client discard-rate heatmap over the worst `topN` entities on each axis
// (ranked by Wilson lower bound above the volume floor). Click a cell to drill.
export default function AgentHeatmap({
  agentKeys,
  websiteKeys,
  matrix,
  agentOverall,
  clientOverall,
  hiddenAgents,
  hiddenClients,
  labelForAgent,
  websiteById,
  selectedAgent,
  selectedWebsite,
  onSelectCell,
  topN,
  minVolume,
}) {
  const websiteName = (wid) => (websiteById[wid] && websiteById[wid].website_name) || wid;
  return (
    <details className='bg-white border border-border-subtle rounded-2xl p-4' open data-testid='agent-heatmap'>
      <summary className='cursor-pointer text-body-sm font-semibold text-text-primary flex items-center gap-2'>
        Agent × Client — discard rate heatmap
        <span className='text-caption font-normal text-text-tertiary'>
          (top {topN} worst on each axis by lower confidence bound{minVolume ? `, ${minVolume}+ issues` : ""}; click a cell to drill)
        </span>
      </summary>
      <div className='flex items-center gap-3 mt-2 mb-3'>
        <span className='text-caption text-text-secondary'>0%</span>
        <div
          className='flex-1 max-w-[200px] h-2 rounded-full'
          style={{ background: "linear-gradient(to right, rgb(31,175,92), rgb(135,121,80), rgb(239,68,68))" }}
        />
        <span className='text-caption text-text-secondary'>100% discarded</span>
        {(hiddenAgents > 0 || hiddenClients > 0) && (
          <span className='text-caption text-text-tertiary ml-auto'>
            {hiddenAgents > 0 && `${hiddenAgents} more agent${hiddenAgents === 1 ? "" : "s"}`}
            {hiddenAgents > 0 && hiddenClients > 0 && " · "}
            {hiddenClients > 0 && `${hiddenClients} more client${hiddenClients === 1 ? "" : "s"}`} hidden (use the ranked lists above)
          </span>
        )}
      </div>
      {agentKeys.length === 0 ? (
        <p className='text-text-tertiary text-body-xs'>No triaged issues in range above the volume floor.</p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='text-caption border-collapse'>
            <thead>
              <tr>
                <th className='text-left p-2 border-b border-border-subtle text-text-secondary sticky left-0 bg-white z-10'>Agent</th>
                {websiteKeys.map((wid) => (
                  <th key={wid} className='p-2 border-b border-border-subtle text-text-secondary text-left whitespace-nowrap' title={wid}>
                    {websiteName(wid).slice(0, 18)}
                  </th>
                ))}
                <th className='p-2 border-b border-border-subtle text-text-secondary text-left whitespace-nowrap bg-surface-neutral-subtle font-semibold'>
                  All clients
                </th>
              </tr>
            </thead>
            <tbody>
              {agentKeys.map((agent) => {
                const label = labelForAgent(agent);
                const overall = agentOverall[agent];
                return (
                  <tr key={agent}>
                    <td
                      className='p-2 border-b border-border-subtle text-text-primary whitespace-nowrap font-medium sticky left-0 bg-white z-10'
                      title={label}>
                      {label.length > 26 ? `${label.slice(0, 26)}…` : label}
                    </td>
                    {websiteKeys.map((wid) => {
                      const cell = matrix[agent] && matrix[agent][wid];
                      const rate = cell && cell.total > 0 ? cell.discarded_count / cell.total : null;
                      const isSel = selectedAgent === agent && selectedWebsite === wid;
                      return (
                        <td
                          key={wid}
                          onClick={() => cell && onSelectCell(agent, wid, isSel)}
                          style={{ backgroundColor: rate != null ? heatColor(rate) : undefined }}
                          className={`p-2 border-b border-border-subtle text-white text-center min-w-[60px] ${
                            rate != null ? "cursor-pointer hover:opacity-80" : "text-text-tertiary"
                          } ${isSel ? "ring-2 ring-accent-purple ring-inset" : ""}`}
                          title={cell ? `${cell.discarded_count} discarded · ${cell.closed_count} closed · ${cell.open_count} open` : "no data"}>
                          {rate != null ? fmtPct(rate) : "—"}
                        </td>
                      );
                    })}
                    <td
                      style={{ backgroundColor: overall && overall.rejectRate != null ? heatColor(overall.rejectRate) : undefined }}
                      className='p-2 border-b border-border-subtle text-white text-center font-semibold whitespace-nowrap'
                      title={`${(overall && overall.total) || 0} total issues across all clients`}>
                      {overall && overall.rejectRate != null ? fmtPct(overall.rejectRate) : "—"}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className='p-2 text-text-secondary font-semibold whitespace-nowrap sticky left-0 bg-surface-neutral-subtle z-10'>All agents</td>
                {websiteKeys.map((wid) => {
                  const overall = clientOverall[wid];
                  return (
                    <td
                      key={wid}
                      style={{ backgroundColor: overall && overall.rejectRate != null ? heatColor(overall.rejectRate) : undefined }}
                      className='p-2 text-white text-center font-semibold'
                      title={`${(overall && overall.total) || 0} total issues across all agents`}>
                      {overall && overall.rejectRate != null ? fmtPct(overall.rejectRate) : "—"}
                    </td>
                  );
                })}
                <td className='p-2 bg-surface-neutral-subtle' />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
